import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, Loader2 } from "lucide-react";
import { fetchTrendingPairs } from "@/lib/api";

/**
 * "Trending pairs this week" strip.
 * Sources from the most-shared calculator URLs (POST /api/trending/track is
 * fired by the Calculator on share and on share-link hydration). Renders
 * nothing until at least one pair has been shared this week so the home
 * page stays clean on a cold start.
 */
export default function TrendingPairs() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchTrendingPairs(4)
      .then((p) => { if (alive) setPairs(p); })
      .catch(() => { if (alive) setPairs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <section className="border-b border-border" data-testid="trending-pairs-loading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          Loading trending pairs…
        </div>
      </section>
    );
  }

  if (!pairs.length) return null;

  return (
    <section className="border-b border-border bg-secondary/40" data-testid="trending-pairs">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-14">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <Flame size={12} className="text-primary" />
              Trending pairs · last 7 days
            </div>
            <h2 className="text-lg md:text-lg font-bold tracking-tight">
              What other breeders are pairing right now
            </h2>
          </div>
          <Link
            to="/calculator"
            data-testid="trending-pairs-build-link"
            className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
          >
            Build your own <ArrowRight size={12} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {pairs.map((p, idx) => {
            const params = new URLSearchParams();
            params.set("m", p.m);
            params.set("f", p.f);
            const href = `/calculator?${params.toString()}`;
            return (
              <Link
                to={href}
                key={`${p.m}|${p.f}`}
                data-testid={`trending-pair-${idx}`}
                className="border border-border bg-card p-4 group hover:border-primary/50 hover:bg-card transition-colors flex flex-col gap-3"
              >
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span>Pair · {String(idx + 1).padStart(2, "0")}</span>
                  <span className="text-primary">
                    {p.share_count}× shared
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground w-4">♂</span>
                    <span className="text-sm font-medium truncate" title={p.male_summary}>
                      {p.male_summary}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground w-4">♀</span>
                    <span className="text-sm font-medium truncate" title={p.female_summary}>
                      {p.female_summary}
                    </span>
                  </div>
                </div>
                <div className="mt-auto pt-2 border-t border-border text-xs text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open pair <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
